/**
 * Bluetooth Thermal Printer — MXW01 Image Protocol
 * Uses mxw01-thermal-printer library for the proprietary MXW01/xofiz protocol.
 * This printer is IMAGE-based, not text-based. Everything is rendered
 * on a canvas and sent as pixel data.
 *
 * Usage:
 *   import printer from '@/utils/bluetooth-printer.js'
 *   await printer.connect()
 *   await printer.printTicket({ funcion, uuid, payment, price, date })
 */
import { ThermalPrinterClient, WebBluetoothAdapter } from 'mxw01-thermal-printer'
const PRINT_WIDTH = 384 // 58mm paper = 384 dots
// Logo for QR center overlay (inverted dancer figure, black on white)
const LOGO_DATA_URL = 'data:image/png;base64,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'
class MXW01Printer {
  constructor() {
    this.adapter = null
    this.client = null
    this.connected = false
    this.printerName = ''
  }
  isSupported() {
    return !!navigator.bluetooth
  }
  async connect() {
    if (!this.isSupported()) {
      throw new Error('Web Bluetooth no disponible')
    }
    this.adapter = new WebBluetoothAdapter()
    this.client = new ThermalPrinterClient(this.adapter)
    this.client.on('connected', (event) => {
      this.connected = true
      this.printerName = event.device?.name || 'MXW01'
    })
    this.client.on('disconnected', () => {
      this.connected = false
    })
    this.client.on('error', (event) => {
      console.error('Printer error:', event.error)
    })
    await this.client.connect()
    this.connected = this.client.isConnected
    return this.connected
  }
  async disconnect() {
    if (this.client) {
      await this.client.disconnect()
    }
    this.connected = false
  }
  /**
   * Print a ticket receipt
   */
  async printTicket(ticket) {
    if (!this.connected || !this.client) {
      throw new Error('Impresora no conectada')
    }
    const canvas = await this._renderTicket(ticket)
    const ctx = canvas.getContext('2d')
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    await this.client.print(imageData, {
      dither: 'threshold',
      brightness: 128,
      intensity: 100,
    })
  }
  /**
   * Print a test receipt
   */
  async printTest() {
    if (!this.connected || !this.client) {
      throw new Error('Impresora no conectada')
    }
    const canvas = this._createCanvas(200)
    const ctx = canvas.getContext('2d')
    // White background
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, PRINT_WIDTH, 200)
    // Header
    ctx.fillStyle = '#000000'
    ctx.font = 'bold 24px Arial'
    ctx.textAlign = 'center'
    ctx.fillText('GRUPO BABILONIA', PRINT_WIDTH / 2, 40)
    ctx.font = '16px Arial'
    ctx.fillText('Test de impresión', PRINT_WIDTH / 2, 70)
    // Dashed line
    this._drawDash(ctx, 90)
    ctx.font = 'bold 18px Arial'
    ctx.fillText('✓ Impresora OK!', PRINT_WIDTH / 2, 120)
    ctx.font = '14px Arial'
    ctx.fillText(new Date().toLocaleString('es-UY'), PRINT_WIDTH / 2, 150)
    this._drawDash(ctx, 170)
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    await this.client.print(imageData, {
      dither: 'threshold',
      brightness: 128,
      intensity: 100,
    })
  }
  /**
   * Render a ticket onto a canvas
   */
  async _renderTicket(ticket) {
    // First, generate QR code image
    const qrDataURL = await this._generateQRDataURL(ticket.uuid)
    // Render on oversized canvas, then crop to actual content
    const maxHeight = 800
    const canvas = this._createCanvas(maxHeight)
    const ctx = canvas.getContext('2d')
    // White background
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, PRINT_WIDTH, maxHeight)
    ctx.fillStyle = '#000000'
    let y = 15
    // ─── Header ───
    ctx.font = 'bold 42px Arial'
    ctx.textAlign = 'center'
    ctx.fillText('GRUPO BABILONIA', PRINT_WIDTH / 2, y + 42)
    y += 55
    ctx.font = 'bold 24px Arial'
    ctx.fillText('Festival Arena y Barro', PRINT_WIDTH / 2, y + 24)
    y += 40
    // ─── Dash ───
    this._drawDash(ctx, y)
    y += 18
    // ─── Show name ───
    ctx.font = 'bold 30px Arial'
    const lines = this._wrapText(ctx, ticket.funcion, PRINT_WIDTH - 30)
    for (const line of lines) {
      ctx.fillText(line, PRINT_WIDTH / 2, y + 30)
      y += 38
    }
    // ─── Dash ───
    this._drawDash(ctx, y + 5)
    y += 22
    // ─── QR Code ───
    if (qrDataURL) {
      const qrImg = await this._loadImage(qrDataURL)
      const qrSize = 320
      const qrX = (PRINT_WIDTH - qrSize) / 2
      ctx.drawImage(qrImg, qrX, y, qrSize, qrSize)
      y += qrSize + 10
    }
    // ─── UUID ───
    ctx.font = '12px Courier New'
    ctx.fillText(ticket.uuid, PRINT_WIDTH / 2, y + 12)
    y += 24
    // ─── Payment + Price ───
    ctx.font = 'bold 22px Arial'
    ctx.textAlign = 'left'
    ctx.fillText(ticket.payment, 20, y + 22)
    ctx.textAlign = 'right'
    ctx.fillText('$' + ticket.price + ' UYU', PRINT_WIDTH - 20, y + 22)
    y += 36
    // ─── Date ───
    ctx.font = '18px Arial'
    ctx.textAlign = 'center'
    ctx.fillText(ticket.date, PRINT_WIDTH / 2, y + 18)
    y += 28
    // ─── Final dash ───
    this._drawDash(ctx, y)
    y += 10
    // Crop canvas to actual content height
    const cropped = this._createCanvas(y)
    const cropCtx = cropped.getContext('2d')
    cropCtx.drawImage(canvas, 0, 0)
    return cropped
  }
  // ─── Canvas Helpers ──────────────────────────
  _createCanvas(height) {
    const canvas = document.createElement('canvas')
    canvas.width = PRINT_WIDTH
    canvas.height = height
    return canvas
  }
  _drawDash(ctx, y) {
    ctx.save()
    ctx.strokeStyle = '#000000'
    ctx.lineWidth = 1
    ctx.setLineDash([4, 4])
    ctx.beginPath()
    ctx.moveTo(10, y)
    ctx.lineTo(PRINT_WIDTH - 10, y)
    ctx.stroke()
    ctx.restore()
  }
  _wrapText(ctx, text, maxWidth) {
    const words = text.split(' ')
    const lines = []
    let current = ''
    for (const word of words) {
      const test = current ? current + ' ' + word : word
      if (ctx.measureText(test).width > maxWidth) {
        if (current) lines.push(current)
        current = word
      } else {
        current = test
      }
    }
    if (current) lines.push(current)
    return lines
  }
  _loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = src
    })
  }
  /**
   * Generate a QR code with circular logo overlay as a data URL
   */
  _generateQRDataURL(text) {
    return new Promise((resolve) => {
      if (!window.QRCode) {
        console.warn('QRCode library not loaded')
        resolve(null)
        return
      }
      const container = document.createElement('div')
      container.style.position = 'fixed'
      container.style.left = '-9999px'
      document.body.appendChild(container)
      // Generate QR with HIGH error correction (supports ~30% coverage by logo)
      new window.QRCode(container, {
        text: text,
        width: 600,
        height: 600,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: window.QRCode.CorrectLevel.H,
      })
      setTimeout(() => {
        const qrCanvas = container.querySelector('canvas')
        if (!qrCanvas) {
          document.body.removeChild(container)
          resolve(null)
          return
        }
        // Draw logo in the center of the QR
        const logoImg = new Image()
        logoImg.onload = () => {
          const ctx = qrCanvas.getContext('2d')
          const logoSize = 300
          const x = (qrCanvas.width - logoSize) / 2
          const y = (qrCanvas.height - logoSize) / 2
          // White circle background behind logo
          ctx.fillStyle = '#ffffff'
          ctx.beginPath()
          ctx.arc(qrCanvas.width / 2, qrCanvas.height / 2, logoSize / 2 + 2, 0, Math.PI * 2)
          ctx.fill()
          // Clip logo to a circle
          ctx.save()
          ctx.beginPath()
          ctx.arc(qrCanvas.width / 2, qrCanvas.height / 2, logoSize / 2, 0, Math.PI * 2)
          ctx.clip()
          // Draw the logo (clipped to circle)
          ctx.drawImage(logoImg, x, y, logoSize, logoSize)
          ctx.restore()
          const dataURL = qrCanvas.toDataURL('image/png')
          document.body.removeChild(container)
          resolve(dataURL)
        }
        logoImg.onerror = () => {
          // If logo fails, return QR without logo
          const dataURL = qrCanvas.toDataURL('image/png')
          document.body.removeChild(container)
          resolve(dataURL)
        }
        logoImg.src = LOGO_DATA_URL
      }, 100)
    })
  }
}
// Singleton
const printer = new MXW01Printer()
export default printer